import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconPlus,
  IconCalendar,
  IconHome,
  IconCreditCard,
  IconReceipt,
  IconMail,
  IconReport
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../../utils/constants';

function OwnersListScreenMantine() {
  console.log('OwnersListScreenMantine component loaded');
  
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalOwners: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ownerToDelete, setOwnerToDelete] = useState(null);
  const [ownerTypes, setOwnerTypes] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Separate useEffect for initial load only
  useEffect(() => {
    console.log('OwnersListScreenMantine initial useEffect triggered');
    
    // Reset selected owner when page loads
    setSelectedOwner(null);
    
    // Test backend connectivity
    fetch(`${API_URL}/owners?limit=1`)
      .then(response => {
        console.log('Backend connectivity test:', response.status, response.ok ? 'OK' : 'FAILED');
      })
      .catch(error => {
        console.error('Backend connectivity test failed:', error);
      });
    
    fetchOwnerTypes();
    fetchOwners();
  }, []);

  // Separate useEffect for search/sort changes
  useEffect(() => {
    console.log('OwnersListScreenMantine search/sort useEffect triggered');
    fetchOwners();
  }, [searchTerm, sortBy, sortOrder]);

  const fetchOwnerTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('Fetching owner types from:', `${API_URL}/owner-types`);
      const response = await fetch(`${API_URL}/owner-types`, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log('Owner types fetched:', data);
        setOwnerTypes(data);
      } else {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        console.error('Failed to fetch owner types:', response.status, errorData);
        setError(errorData.message || 'Failed to fetch owner types');
      }
    } catch (error) {
      console.error('Error fetching owner types:', error);
      setError(`Error fetching owner types: ${error.message || 'Network error'}`);
    }
  };

  const fetchOwners = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const params = new URLSearchParams({
        limit: 1000, // Load all owners
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const url = `${API_URL}/owners?${params}`;
      console.log('Fetching owners from:', url);

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log('Owners fetched:', data);
        setOwners(data.owners || []);
        setPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalOwners: data.owners?.length || 0,
          hasNextPage: false,
          hasPrevPage: false
        });
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('Failed to fetch owners:', response.status, errorData);
        setError(errorData.message || `Error fetching owners (${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
      setError(`Error fetching owners: ${error.message || 'Network error. Please check that the backend server is running.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleEdit = (owner) => {
    setEditingOwner({ ...owner });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/owners/${editingOwner.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editingOwner),
      });

      if (response.ok) {
        const updatedOwner = await response.json();
        setOwners(prev => prev.map(owner => 
          owner.id === updatedOwner.owner.id ? updatedOwner.owner : owner
        ));
        setShowEditModal(false);
        setEditingOwner(null);
        notifications.show({
          title: 'Success',
          message: 'Owner updated successfully',
          color: 'green',
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error updating owner');
      }
    } catch (error) {
      console.error('Error updating owner:', error);
      setError('Error updating owner. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingOwner(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingOwner(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (owner) => {
    setOwnerToDelete(owner);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/owners/${ownerToDelete.id}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        setOwners(prev => prev.filter(owner => owner.id !== ownerToDelete.id));
        setShowDeleteModal(false);
        setOwnerToDelete(null);
        notifications.show({
          title: 'Success',
          message: 'Owner deleted successfully',
          color: 'green',
        });
        // Refresh the list if current page becomes empty
        if (owners.length === 1 && pagination.currentPage > 1) {
          setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error deleting owner');
      }
    } catch (error) {
      console.error('Error deleting owner:', error);
      setError('Error deleting owner. Please try again.');
    }
  };

  const handleOwnerSelect = (owner) => {
    setSelectedOwner(owner);
    setActiveTab('details');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (loading && owners.length === 0) {
    return (
      <Container fluid p={0} style={{ height: '100vh' }}>
        <Group justify="center" style={{ height: '100%' }}>
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Owners</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => window.location.href = '/dashboard/owners-find'}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
              onClick={() => window.location.href = '/dashboard/owners-add'}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedOwner && handleEdit(selectedOwner)}
              disabled={!selectedOwner}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedOwner && handleDelete(selectedOwner)}
              disabled={!selectedOwner}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Additional buttons */}
            <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              leftSection={<IconCalendar size="0.8rem" />}
            >
              Month End
            </Button>
            <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              leftSection={<IconHome size="0.8rem" />}
            >
              Timeshare
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Financial buttons */}
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
            >
              Post Charge
            </Button>
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
            >
              Global Post
            </Button>
            <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Payment
            </Button>
            <Button 
              variant="subtle" 
              color="pink"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Refund
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* New Reservation button */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
            >
              New Reservation
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Communication and Reports buttons */}
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconMail size="0.8rem" />}
            >
              Emails
            </Button>
            <Button 
              variant="subtle" 
              color="gray"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Owners List</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md">
                <TextInput
                  placeholder="Search owners by name, number, or email..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => setSearchTerm('')} disabled={!searchTerm}>
                  Clear
                </Button>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th onClick={() => handleSort('owner_number')} style={{ cursor: 'pointer' }}>
                      Owner # {getSortIcon('owner_number')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                      Name {getSortIcon('name')}
                    </Table.Th>
                    <Table.Th>Owner Type</Table.Th>
                    <Table.Th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                      Email {getSortIcon('email')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('phone1')} style={{ cursor: 'pointer' }}>
                      Phone 1 {getSortIcon('phone1')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>
                      City {getSortIcon('city')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('active')} style={{ cursor: 'pointer' }}>
                      Status {getSortIcon('active')}
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {owners.map((owner) => (
                    <Table.Tr 
                      key={owner.id} 
                      onClick={() => handleOwnerSelect(owner)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedOwner && selectedOwner.id === owner.id ? '#ffff00' : '',
                        border: selectedOwner && selectedOwner.id === owner.id ? '2px solid #ff0000' : ''
                      }}
                    >
                      <Table.Td><strong>{owner.owner_number}</strong></Table.Td>
                      <Table.Td>{owner.name}</Table.Td>
                      <Table.Td>{owner.owner_type_name || 'N/A'}</Table.Td>
                      <Table.Td>{owner.email || 'N/A'}</Table.Td>
                      <Table.Td>{owner.phone1 || 'N/A'}</Table.Td>
                      <Table.Td>{owner.city || 'N/A'}</Table.Td>
                      <Table.Td>
                        <Badge color={owner.active ? 'green' : 'gray'}>
                          {owner.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon 
                            color="orange" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleEdit(owner); }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon 
                            color="red" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDelete(owner); }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {pagination.totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={pagination.currentPage}
                    onChange={handlePageChange}
                    total={pagination.totalPages}
                    size="sm"
                  />
                </Group>
              )}
            </Paper>

            {/* Owner Details Section */}
            {selectedOwner && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="ownership">Ownership</Tabs.Tab>
                    <Tabs.Tab value="transactions">Transactions</Tabs.Tab>
                    <Tabs.Tab value="reservations">Reservations</Tabs.Tab>
                    <Tabs.Tab value="notes">Notes</Tabs.Tab>
                  </Tabs.List>
                  
                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Owner Number:</strong> {selectedOwner.owner_number}</Text>
                          <Text><strong>Name:</strong> {selectedOwner.name} 
                            <Badge color={selectedOwner.active ? 'green' : 'red'} ml="xs">
                              {selectedOwner.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </Text>
                          <Text><strong>Owner Type:</strong> {selectedOwner.owner_type_name || 'N/A'}</Text>
                          <Text><strong>Email:</strong> {selectedOwner.email || 'N/A'}</Text>
                          <Text><strong>Phone 1:</strong> {selectedOwner.phone1 || 'N/A'}</Text>
                          <Text><strong>Phone 2:</strong> {selectedOwner.phone2 || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Address:</strong></Text>
                          <Text>{selectedOwner.address1 || 'N/A'}</Text>
                          {selectedOwner.address2 && selectedOwner.address2 !== 'N/A' && (
                            <Text>{selectedOwner.address2}</Text>
                          )}
                          <Text>{selectedOwner.city || 'N/A'}, {selectedOwner.state || 'N/A'} {selectedOwner.zipcode || 'N/A'}</Text>
                          <Text>{selectedOwner.country || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                    
                    <Divider my="md" />
                    
                    <Text><strong>Create Date:</strong> {selectedOwner.create_date ? new Date(selectedOwner.create_date).toLocaleDateString() : 'N/A'}</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="ownership" pt="md">
                    <Text>Ownership Details</Text>
                    <Text size="sm" c="dimmed">This tab will contain ownership information, property details, and investment data.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="transactions" pt="md">
                    <Text>Transaction History</Text>
                    <Text size="sm" c="dimmed">This tab will contain transaction records, payment history, and financial information.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="reservations" pt="md">
                    <Text>Reservation History</Text>
                    <Text size="sm" c="dimmed">This tab will contain reservation history, upcoming stays, and booking information.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="notes" pt="md">
                    <Text>Internal Notes</Text>
                    <Text size="sm" c="dimmed">This tab will contain internal notes, communication history, and important information.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Edit Owner Modal */}
      <Modal opened={showEditModal} onClose={handleCancelEdit} title="Edit Owner" size="xl">
        {editingOwner && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="Owner Number"
                  value={editingOwner.owner_number}
                  disabled
                  description="Owner number cannot be changed"
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Select
                  label="Owner Type"
                  name="owner_type_id"
                  value={editingOwner.owner_type_id || ''}
                  onChange={(value) => setEditingOwner(prev => ({ ...prev, owner_type_id: value }))}
                  data={ownerTypes.map(type => ({ value: type.id.toString(), label: type.name }))}
                  placeholder="Select Owner Type"
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="Status"
                  name="active"
                  type="checkbox"
                  checked={editingOwner.active}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Name"
                  name="name"
                  value={editingOwner.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Email"
                  name="email"
                  value={editingOwner.email || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 1"
                  name="phone1"
                  value={editingOwner.phone1 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Phone 2"
                  name="phone2"
                  value={editingOwner.phone2 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 1"
                  name="address1"
                  value={editingOwner.address1 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Address 2"
                  name="address2"
                  value={editingOwner.address2 || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="City"
                  name="city"
                  value={editingOwner.city || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="State"
                  name="state"
                  value={editingOwner.state || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="Country"
                  name="country"
                  value={editingOwner.country || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  label="Zipcode"
                  name="zipcode"
                  value={editingOwner.zipcode || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Stack gap="md">
          <Text>
            Are you sure you want to delete owner <strong>{ownerToDelete?.owner_number}</strong> ({ownerToDelete?.name})?
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default OwnersListScreenMantine;


